import React from 'react'
import PropTypes from 'prop-types'
import styled, { keyframes } from 'styled-components'
import { Box, Flex, Copy, Icon, themeColor, DotsLoader } from '@elparking/components'

const Comment = styled(Box)`
    padding: 0.2em 0.5em;
    border-radius: 3px;
    display: inline-block;
    background: ${({ color }) => themeColor(color)};
    color: ${themeColor('white')};
    position: relative;
    &:after {
        position: absolute;
        font-size: 10px;
        left: ${({left}) => left ? '0.5em' : 'calc(100% - 1.5em)'};
        color: ${({ color }) => themeColor(color)};
        content: '▼';
    }
`

Comment.propTypes = {
    left: PropTypes.bool.isRequired,
}

Comment.defaultProps = {
    left: false,
}

const opacityAnimation = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`

const StyledBox = styled(Flex)`
animation-name: opacityAnimation;
animation: ${opacityAnimation} ${({time}) => time};
`

StyledBox.defaultProps = {
    time: '2s',
}

StyledBox.propTypes = {
    time: PropTypes.string.isRequired,
}

const StyledContainer = styled(StyledBox)``

StyledContainer.defaultProps = {
    time: '0.5s',
}

const File = styled(Flex)`
    height: 100px;
    border-radius: 3px;
    background-color: ${themeColor('gray80')};
    &:hover {
        background-color: ${themeColor('gray')};
        box-shadow: 0px 2px 8px 0px ${themeColor('gray')};
    }
`

const Image = styled(File)`
    background-image: url(${({image}) => image});
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
`

Image.propTypes = {
    image: PropTypes.string.isRequired,
}

const Container = ({ children, width }) => (<StyledContainer justifyContent='center'>
    <Box p={['0 1em', '0 2em']} width={width} maxWidth={width}>
        {children}
    </Box>
</StyledContainer>)

Container.propTypes = {
    children: PropTypes.node.isRequired,
    width: PropTypes.string.isRequired,
}

Container.defaultProps = {
    width: '400px',
}

const Loader = ({ children }) => (<Container key='loading'>
    <Box textAlign='center'>
        <DotsLoader />
        <Copy fontSize='large' fontColor='gray60' fontWeight='regular'>
            { children }
        </Copy>
    </Box>
</Container>)

Loader.propTypes = {
    children: PropTypes.node.isRequired,
}

const mimeTypeMap = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
}

const Files = ({ bulletinNumber, files }) => files.map(({ encodedFile, mimeType }, index) => {
    const fileExtension = mimeTypeMap[mimeType] || 'bin'
    const fileMimeType = mimeType || 'application/octet-stream'
    const fileName = `${bulletinNumber}_file_${index + 1}.${fileExtension}`
    const fileUrl = `data:${fileMimeType};base64,${encodedFile}`
    const isImageType = Object.keys(mimeTypeMap).includes(mimeType)
    return (
        <Box key={index} p='0.5em' w={['100%', '50%']} >
            <a download={fileName} href={fileUrl}>
                { isImageType
                    ? <Image justifyContent='space-between' p='0.5em 1em' image={fileUrl} alt='Alt' />
                    : <File justifyContent='center'>
                        <Copy textAlign='center' fontSize='xSmall' padding='1.5em 0'>
                            <Icon size='xxLarge' color='white' type='download' />
                            <Copy fontColor='white' fontSize='xSmall' padding='1em 0 0 0'>
                                {fileName}
                            </Copy>
                        </Copy>
                    </File>
                }
            </a>
        </Box>)
})

Files.propTypes = {
    bulletinNumber: PropTypes.string,
    files: PropTypes.array,
}

Files.defaultProps = {
    files: [],
}

export {
    StyledBox,
    Comment,
    Container,
    Loader,
    Files,
}
